import PropTypes from 'prop-types';

import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import Box from '@zenbusiness/zui/Box';
import CardHeader from '@zenbusiness/zui/CardHeader';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import { useTheme } from '@zenbusiness/zui/styles';

import { formatEstimate } from '#client/components/NextUp/utils/formatEstimate';
import { generateZenUrl } from '#client/utils/urls';

import * as Styled from './EnterpriseNextUpCard.style';

function EnterpriseNextUpCard({
  ctaType,
  ctaUrl,
  description,
  estimate,
  highlight,
  imageUrl,
  isLocked,
  title
}) {
  const { palette } = useTheme();

  const linkPressedCallback = useLinkPressedCallback({
    appSection: 'home',
    destination: ctaUrl,
    elementName: 'enterprise_next_up_card',
    elementText: title
  });

  const fullUrl = ctaUrl && !ctaUrl.startsWith('http') ? generateZenUrl(ctaUrl) : ctaUrl;
  const linkProps = ctaType === 'modal' ? { to: ctaUrl } : { href: fullUrl };

  const cardContent = (
    <Styled.CardContentStyled highlight={highlight}>
      <Styled.CardContainer gap={2}>
        <Styled.LogoContainer>
          <SharedAsset alt="" src={imageUrl} />
        </Styled.LogoContainer>

        <Styled.Content gap={2}>
          <CardHeader subtitle={description} title={title} />
          <Box>
            <Styled.HighlightChip
              highlight={highlight}
              icon={(
                <SharedAsset
                  alt="clock-icon"
                  color={highlight ? palette.fixed.white : palette.fixed.black}
                  src="/s2/icon/clock-fill.svg"
                  width={16}
                />
              )}
              size="sm"
            >
              {formatEstimate(estimate)}
            </Styled.HighlightChip>
          </Box>
        </Styled.Content>
      </Styled.CardContainer>
    </Styled.CardContentStyled>
  );

  if (isLocked) {
    return (
      <Styled.LockedCard aria-label={title}>
        {cardContent}
      </Styled.LockedCard>
    );
  }

  return (
    <Styled.InteractiveCard
      aria-label={title}
      component={Link}
      interactive
      onClick={linkPressedCallback}
      target={ctaType === 'external' ? '_blank' : '_self'}
      {...linkProps}
    >
      {cardContent}
    </Styled.InteractiveCard>
  );
}

EnterpriseNextUpCard.propTypes = {
  ctaType: PropTypes.string,
  ctaUrl: PropTypes.string,
  description: PropTypes.string,
  estimate: PropTypes.string,
  highlight: PropTypes.bool,
  imageUrl: PropTypes.string,
  isLocked: PropTypes.bool,
  title: PropTypes.string.isRequired
};

export default EnterpriseNextUpCard;
